import React from 'react';
import { Link } from 'react-router-dom';
import HelpBadge from './components/HelpBadge';
import {
  OFAC_VENEZUELA_FLOW_LINKS,
  OFAC_VENEZUELA_WINDOW_COPY,
  OFAC_VENEZUELA_WINDOW_SOURCES,
  ofacVenezuelaSourceLabel,
  ofacVenezuelaWindowText,
  type OfacLanguage,
} from './ofacVenezuelaWindowCopy';

type OfacVenezuelaWindowProps = {
  language: OfacLanguage;
  variant?: 'product' | 'subpage' | 'industry';
  demoTo?: string;
};

const OfacVenezuelaWindow: React.FC<OfacVenezuelaWindowProps> = ({
  language,
  variant = 'product',
  demoTo = '/demo-request',
}) => {
  const copy = OFAC_VENEZUELA_WINDOW_COPY;
  const headingId = 'ofac-venezuela-window-title';
  const compact = variant !== 'product';

  return (
    <section
      className={`ofac-venezuela-window ofac-venezuela-window--${variant}${compact ? ' is-compact' : ''}`}
      aria-labelledby={headingId}
    >
      <header className="ofac-venezuela-window__header">
        <p className="ofac-venezuela-window__kicker">{ofacVenezuelaWindowText(language, copy.kicker)}</p>
        <h2 id={headingId} className="ofac-venezuela-window__title">
          <span>{ofacVenezuelaWindowText(language, copy.title)}</span>
          <HelpBadge
            label={language === 'es' ? 'Ayuda sobre la ventana Venezuela' : 'Help about the Venezuela window'}
            wide
          >
            <p>{ofacVenezuelaWindowText(language, copy.help)}</p>
          </HelpBadge>
        </h2>
        <p>{ofacVenezuelaWindowText(language, copy.lead)}</p>
      </header>

      <div className="ofac-venezuela-window__grid" aria-label={ofacVenezuelaWindowText(language, copy.conceptsTitle)}>
        {copy.concepts.map((concept) => (
          <article key={concept.id} className={`ofac-venezuela-window__card is-${concept.id}`}>
            <h3>{ofacVenezuelaWindowText(language, concept.title)}</h3>
            <p>{ofacVenezuelaWindowText(language, concept.body)}</p>
          </article>
        ))}
      </div>

      <div className="ofac-venezuela-window__split">
        <div>
          <h3>{ofacVenezuelaWindowText(language, copy.licenseFactsTitle)}</h3>
          <ul>
            {copy.licenseFacts.map((fact) => (
              <li key={fact.en}>{ofacVenezuelaWindowText(language, fact)}</li>
            ))}
          </ul>
        </div>
        <div>
          <h3>{ofacVenezuelaWindowText(language, copy.testsTitle)}</h3>
          <ol>
            {copy.tests.map((test) => (
              <li key={test.en}>{ofacVenezuelaWindowText(language, test)}</li>
            ))}
          </ol>
        </div>
      </div>

      {variant !== 'subpage' ? (
        <nav className="ofac-venezuela-window__flow" aria-label={ofacVenezuelaWindowText(language, copy.flowTitle)}>
          <p className="ofac-venezuela-window__flow-label">{ofacVenezuelaWindowText(language, copy.flowTitle)}</p>
          <ul>
            {OFAC_VENEZUELA_FLOW_LINKS.map((link) => (
              <li key={link.id}>
                <Link to={link.to}>{language === 'es' ? link.labelEs : link.labelEn}</Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      <footer className="ofac-venezuela-window__footer">
        <div className="ofac-venezuela-window__actions">
          {variant === 'industry' ? (
            <Link to="/products/ofac-compliance" className="btn btn-primary">
              {ofacVenezuelaWindowText(language, copy.productCta)}
            </Link>
          ) : (
            <Link to={demoTo} className="btn btn-primary">
              {ofacVenezuelaWindowText(language, copy.demoCta)}
            </Link>
          )}
        </div>
        <ul className="ofac-venezuela-window__sources">
          {OFAC_VENEZUELA_WINDOW_SOURCES.map((source) => (
            <li key={source.id}>
              <a href={source.href} target="_blank" rel="noreferrer">
                {ofacVenezuelaSourceLabel(language, source)}
              </a>
            </li>
          ))}
        </ul>
      </footer>
    </section>
  );
};

export default OfacVenezuelaWindow;
